import { mutations } from "@/graphql";
import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { enqueueSnackbar } from "notistack";
import type { MutationDeleteRoleArgs } from "@/types/graphql";

/**
 * Custom hook to delete a role.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @returns {{deleteRole: Function, deleting: boolean}} - The function to delete a role and the loading state.
 */
export function useDeleteRole(
  onCompleted: (data?: any) => void = () => {},
  onError: (error?: any) => void = () => {},
) {
  const [deleteRole, { loading }] = useMutation<any, MutationDeleteRoleArgs>(
    mutations.deleteRole,
    {
      onCompleted,
      onError: (err) => {
        onError(err);

        enqueueSnackbar("Delete Role: " + err.message, {
          variant: "error",
        });
      },
      errorPolicy: "all",
    },
  );

  const deleteRoleRow = useCallback(
    (variables: MutationDeleteRoleArgs) => {
      deleteRole({
        variables,
        refetchQueries: ["getAllRoles"],
      }).then();
    },
    [deleteRole],
  );

  return {
    deleteRole: deleteRoleRow,
    deleting: loading,
  };
}
